/**
 * Admin Stripe products management page.
 * Lets sysadmins manage boost packs and subscription tiers.
 */

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import {
  useAdminStripeProducts,
  useAdminUpdateStripeProduct,
  useAdminUpdateStripePrice,
  useAdminCreateStripeProduct,
  useAdminCreateStripePrice,
} from "../hooks/useAdminStripe";
import type { AdminStripeProduct, AdminStripePrice } from "../services/naraDoor";
import {
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  Save,
  X,
  Package,
  DollarSign,
  Zap,
} from "lucide-react";
import { publicRoutes } from "../lib/routes";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatAmount(cents: number | null, currency: string): string {
  if (cents === null) return "N/A";
  const amount = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active
          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function TypeBadge({ type, interval }: { type: string; interval?: string }) {
  const label =
    type === "recurring" && interval
      ? `Recurring / ${interval}`
      : type === "one_time"
        ? "One-time"
        : type;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
      {label}
    </span>
  );
}

// ─── Create Product Form ────────────────────────────────────────────────────

function CreateProductForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createProduct = useAdminCreateStripeProduct();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createProduct.mutate(
      { name, description: description || undefined },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-4 mb-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">New Product</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!name.trim() || createProduct.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-nara-orange text-nara-charcoal rounded-lg hover:bg-nara-orange/90 disabled:opacity-50"
        >
          {createProduct.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create
        </button>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">
          Cancel
        </button>
      </div>
      {createProduct.isError && (
        <p className="mt-2 text-sm text-red-600">{createProduct.error.message}</p>
      )}
    </form>
  );
}

// ─── Create Price Form ──────────────────────────────────────────────────────

function CreatePriceForm({
  productId,
  onClose,
}: {
  productId: string;
  onClose: () => void;
}) {
  const [unitAmount, setUnitAmount] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [type, setType] = useState<"one_time" | "recurring">("one_time");
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [boosts, setBoosts] = useState("");
  const createPrice = useAdminCreateStripePrice();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(unitAmount) * 100);
    createPrice.mutate(
      {
        productId,
        unitAmount: amountCents,
        currency,
        type,
        boosts: parseInt(boosts, 10),
        ...(type === "recurring" && { interval }),
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="bg-muted/30 border border-border rounded-lg p-3 mt-3">
      <h4 className="text-xs font-semibold text-foreground mb-2">New Price</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Amount ($) *</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={unitAmount}
            onChange={(e) => setUnitAmount(e.target.value)}
            required
            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg"
          >
            <option value="usd">USD</option>
            <option value="cad">CAD</option>
            <option value="eur">EUR</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Boosts *</label>
          <input
            type="number"
            min="0"
            value={boosts}
            onChange={(e) => setBoosts(e.target.value)}
            required
            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as "one_time" | "recurring")}
            className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg"
          >
            <option value="one_time">One-time</option>
            <option value="recurring">Recurring</option>
          </select>
        </div>
        {type === "recurring" && (
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Interval</label>
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value as "month" | "year")}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded-lg"
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!unitAmount || !boosts || createPrice.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-nara-orange text-nara-charcoal rounded-lg hover:bg-nara-orange/90 disabled:opacity-50"
        >
          {createPrice.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Add Price
        </button>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
          Cancel
        </button>
      </div>
      {createPrice.isError && (
        <p className="mt-2 text-xs text-red-600">{createPrice.error.message}</p>
      )}
    </form>
  );
}

// ─── Editable Product Card ──────────────────────────────────────────────────

function ProductCard({ product }: { product: AdminStripeProduct }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showCreatePrice, setShowCreatePrice] = useState(false);
  const [editName, setEditName] = useState(product.name);
  const [editDescription, setEditDescription] = useState(product.description || "");

  const updateProduct = useAdminUpdateStripeProduct();
  const updatePrice = useAdminUpdateStripePrice();

  const handleSaveProduct = () => {
    updateProduct.mutate(
      {
        productId: product.id,
        name: editName,
        description: editDescription || undefined,
      },
      {
        onSuccess: () => setEditing(false),
      }
    );
  };

  const handleToggleProductActive = () => {
    updateProduct.mutate({
      productId: product.id,
      active: !product.active,
    });
  };

  const handleTogglePriceActive = (price: AdminStripePrice) => {
    updatePrice.mutate({
      priceId: price.id,
      active: !price.active,
    });
  };

  const handleUpdatePriceBoosts = (price: AdminStripePrice, newBoosts: number) => {
    updatePrice.mutate({
      priceId: price.id,
      boosts: newBoosts,
    });
  };

  const handleUpdatePriceMetadata = (
    price: AdminStripePrice,
    updates: {
      showOnPricing?: boolean;
      displayName?: string;
      displayDescription?: string;
      popular?: boolean;
      sortOrder?: number;
    }
  ) => {
    updatePrice.mutate({
      priceId: price.id,
      ...updates,
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Product header */}
      <div className="flex items-center gap-3 p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Package className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="px-2 py-1 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
              />
              <input
                type="text"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Description"
                className="px-2 py-1 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nara-orange/50 flex-1"
              />
              <button
                onClick={handleSaveProduct}
                disabled={updateProduct.isPending}
                className="p-1 text-green-600 hover:text-green-700"
              >
                {updateProduct.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </button>
              <button onClick={() => setEditing(false)} className="p-1 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div>
              <button
                onClick={() => {
                  setEditName(product.name);
                  setEditDescription(product.description || "");
                  setEditing(true);
                }}
                className="text-sm font-medium text-foreground hover:underline text-left"
              >
                {product.name}
              </button>
              {product.description && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{product.description}</p>
              )}
            </div>
          )}
        </div>
        <ActiveBadge active={product.active} />
        <button
          onClick={handleToggleProductActive}
          disabled={updateProduct.isPending}
          className="text-xs px-2 py-1 border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-50"
        >
          {product.active ? "Deactivate" : "Activate"}
        </button>
        <span className="text-xs text-muted-foreground font-mono">{product.id}</span>
      </div>

      {updateProduct.isError && (
        <p className="px-4 pb-2 text-xs text-red-600">{updateProduct.error.message}</p>
      )}

      {/* Prices table */}
      {expanded && (
        <div className="border-t border-border px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Prices ({product.prices.length})
            </h4>
            <button
              onClick={() => setShowCreatePrice(!showCreatePrice)}
              className="inline-flex items-center gap-1 text-xs text-nara-orange hover:text-nara-orange/80 font-medium"
            >
              <Plus className="h-3 w-3" />
              New Price
            </button>
          </div>

          {product.prices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="pb-2 pr-4 font-medium">Amount</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Boosts</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Show</th>
                    <th className="pb-2 pr-4 font-medium">Popular</th>
                    <th className="pb-2 pr-4 font-medium">Price ID</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {product.prices.map((price) => (
                    <PriceRow
                      key={price.id}
                      price={price}
                      onToggleActive={() => handleTogglePriceActive(price)}
                      onUpdateBoosts={(b) => handleUpdatePriceBoosts(price, b)}
                      onUpdatePriceMetadata={(updates) => handleUpdatePriceMetadata(price, updates)}
                      isUpdating={updatePrice.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-2">No prices configured.</p>
          )}

          {showCreatePrice && (
            <CreatePriceForm
              productId={product.id}
              onClose={() => setShowCreatePrice(false)}
            />
          )}

          {updatePrice.isError && (
            <p className="mt-2 text-xs text-red-600">{updatePrice.error.message}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Price Row ──────────────────────────────────────────────────────────────

function PriceRow({
  price,
  onToggleActive,
  onUpdateBoosts,
  onUpdatePriceMetadata,
  isUpdating,
}: {
  price: AdminStripePrice;
  onToggleActive: () => void;
  onUpdateBoosts: (boosts: number) => void;
  onUpdatePriceMetadata: (updates: {
    showOnPricing?: boolean;
    displayName?: string;
    displayDescription?: string;
    popular?: boolean;
    sortOrder?: number;
  }) => void;
  isUpdating: boolean;
}) {
  const [editingBoosts, setEditingBoosts] = useState(false);
  const [boostsValue, setBoostsValue] = useState(String(price.boosts ?? ""));
  const [displayName, setDisplayName] = useState(price.displayName ?? "");
  const [displayDescription, setDisplayDescription] = useState(price.displayDescription ?? "");
  const [sortOrder, setSortOrder] = useState(String(price.sortOrder ?? ""));

  // Drafts follow the server value whenever it changes (a save, another admin, a refetch).
  useEffect(() => setDisplayName(price.displayName ?? ""), [price.displayName]);
  useEffect(() => setDisplayDescription(price.displayDescription ?? ""), [price.displayDescription]);
  useEffect(() => setSortOrder(String(price.sortOrder ?? "")), [price.sortOrder]);

  const handleBoostsSave = () => {
    const val = parseInt(boostsValue, 10);
    if (!isNaN(val) && val >= 0) {
      onUpdateBoosts(val);
      setEditingBoosts(false);
    }
  };

  const handleDisplayNameBlur = () => {
    if (displayName !== (price.displayName ?? "")) {
      onUpdatePriceMetadata({ displayName });
    }
  };

  const handleDisplayDescriptionBlur = () => {
    if (displayDescription !== (price.displayDescription ?? "")) {
      onUpdatePriceMetadata({ displayDescription });
    }
  };

  const handleSortOrderBlur = () => {
    const val = parseInt(sortOrder, 10);
    if (!isNaN(val) && val !== price.sortOrder) {
      onUpdatePriceMetadata({ sortOrder: val });
    }
  };

  return (
    <>
      <tr className="border-b border-border/50 last:border-0">
        <td className="py-2 pr-4">
          <span className="inline-flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            {formatAmount(price.unitAmount, price.currency)}
          </span>
        </td>
        <td className="py-2 pr-4">
          <TypeBadge type={price.type} interval={price.interval} />
        </td>
        <td className="py-2 pr-4">
          {editingBoosts ? (
            <span className="inline-flex items-center gap-1">
              <input
                type="number"
                min="0"
                value={boostsValue}
                onChange={(e) => setBoostsValue(e.target.value)}
                className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-nara-orange/50"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBoostsSave();
                  if (e.key === "Escape") setEditingBoosts(false);
                }}
              />
              <button onClick={handleBoostsSave} className="text-green-600 hover:text-green-700">
                <Save className="h-3 w-3" />
              </button>
              <button onClick={() => setEditingBoosts(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <button
              onClick={() => {
                setBoostsValue(String(price.boosts ?? 0));
                setEditingBoosts(true);
              }}
              className="inline-flex items-center gap-1 hover:underline"
              title="Click to edit boosts"
            >
              <Zap className="h-3 w-3 text-yellow-500" />
              <span className="text-sm">{price.boosts ?? "N/A"}</span>
            </button>
          )}
        </td>
        <td className="py-2 pr-4">
          <ActiveBadge active={price.active} />
        </td>
        <td className="py-2 pr-4">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={price.showOnPricing}
              onChange={(e) => onUpdatePriceMetadata({ showOnPricing: e.target.checked })}
              disabled={isUpdating}
              className="rounded border-border text-nara-orange focus:ring-nara-orange/50"
            />
            <span className="text-xs text-muted-foreground">Pricing</span>
          </label>
        </td>
        <td className="py-2 pr-4">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={price.popular}
              onChange={(e) => onUpdatePriceMetadata({ popular: e.target.checked })}
              disabled={isUpdating}
              className="rounded border-border text-nara-orange focus:ring-nara-orange/50"
            />
            <span className="text-xs text-muted-foreground">Popular</span>
          </label>
        </td>
        <td className="py-2 pr-4">
          <span className="text-xs text-muted-foreground font-mono">{price.id}</span>
        </td>
        <td className="py-2">
          <button
            onClick={onToggleActive}
            disabled={isUpdating}
            className="text-xs px-2 py-0.5 border border-border rounded text-muted-foreground hover:text-foreground hover:border-foreground/30 disabled:opacity-50"
          >
            {price.active ? "Deactivate" : "Activate"}
          </button>
        </td>
      </tr>
      {/* Display metadata row */}
      <tr className="border-b border-border/50 last:border-0">
        <td colSpan={8} className="py-1 px-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <label className="text-xs text-muted-foreground whitespace-nowrap">Name:</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onBlur={handleDisplayNameBlur}
                placeholder="Display name"
                className="w-28 px-1.5 py-0.5 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-nara-orange/50"
              />
            </div>
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <label className="text-xs text-muted-foreground whitespace-nowrap">Features:</label>
              <input
                type="text"
                value={displayDescription}
                onChange={(e) => setDisplayDescription(e.target.value)}
                onBlur={handleDisplayDescriptionBlur}
                placeholder="Comma-separated features"
                className="flex-1 min-w-0 px-1.5 py-0.5 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-nara-orange/50"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-muted-foreground whitespace-nowrap">Order:</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                onBlur={handleSortOrderBlur}
                placeholder="#"
                className="w-14 px-1.5 py-0.5 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-nara-orange/50"
              />
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function AdminStripeProducts() {
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const { data, isLoading, error } = useAdminStripeProducts();
  const [showCreateProduct, setShowCreateProduct] = useState(false);

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  return (
    <div>
      <PageHeader
        title="Stripe Products"
        description="Manage boost packs and subscription tiers"
        isAdmin
        breadcrumbs={[
          { label: "Dashboard", href: publicRoutes.home },
          { label: "Admin" },
          { label: "Stripe Products" },
        ]}
      />

      <div className="p-6 max-w-6xl">
        {/* Actions bar */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {data?.products
              ? `${data.products.length} product${data.products.length !== 1 ? "s" : ""}`
              : ""}
          </p>
          <button
            onClick={() => setShowCreateProduct(!showCreateProduct)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-nara-orange text-nara-charcoal rounded-lg hover:bg-nara-orange/90"
          >
            <Plus className="h-4 w-4" />
            New Product
          </button>
        </div>

        {showCreateProduct && (
          <CreateProductForm onClose={() => setShowCreateProduct(false)} />
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-600">{(error as Error).message}</p>
          </div>
        )}

        {/* Products list */}
        {data?.products && (
          <div className="space-y-3">
            {data.products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
            {data.products.length === 0 && !isLoading && (
              <p className="text-center text-muted-foreground py-8">
                No Stripe products found.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
